/**
 * Sysadmin Administrators → Sessions (Phase 5 PR-E).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.10 (Administrators rail) + the Phase 5
 * architect plan §2.6 (first-rail rationale):
 *   - Active sysadmin sessions roster (refresh_tokens.context='sysadmin')
 *   - Force-revoke flow gated by <TypedConfirmDialog> requiring the
 *     admin email to be typed for confirmation
 *   - 10-second auto-refresh (matches useSysadminServices cadence)
 *
 * Built entirely on PR-0 canonical primitives (no bespoke chrome):
 *   - <Toolbar>          — flat filter row, sticky, no Card wrap
 *   - <StatTile>         — 4-up summary strip
 *   - <StatusPill>       — Active / Zombie / MFA badges
 *   - <EmptyState>       — when filters return zero rows
 *   - <PageError>        — fetch-error surface
 *   - <TypedConfirmDialog> — revoke confirmation
 *
 * Anti-pattern §11.5: the table is NOT wrapped in <Card>. Table rows
 * stay flush with the toolbar so vertical density stays high.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  MonitorSmartphone, RefreshCw, X, Trash2, AlertTriangle,
  Inbox, ShieldCheck,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Toolbar } from '@/components/ui/toolbar';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  useSysadminSessions, useRevokeSysadminSession,
  type SysadminSessionRow,
} from '@/lib/use-sysadmins';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/sysadmin/administrators/sessions')({
  component: SessionsPage,
});

// ─── Page ────────────────────────────────────────────────────────────────────

function SessionsPage() {
  // Filter state is split into "draft" (what the user is typing) and
  // "committed" (what's actually sent to the server). This mirrors the
  // audit-stream UX so the table doesn't re-fetch on every keystroke.
  const [draft, setDraft] = useState({ adminEmail: '', ipAddress: '' });
  const [committed, setCommitted] = useState<{
    adminEmail?: string;
    ipAddress?: string;
  }>({});
  const [revokeTarget, setRevokeTarget] = useState<SysadminSessionRow | null>(
    null,
  );

  const { user } = useAuth();
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminSessions({ ...committed, limit: 50 });
  const revoke = useRevokeSysadminSession();

  const items = data?.items ?? [];

  // 4-up summary tiles. Counts are derived from the visible page only;
  // a true cross-page count would need a separate /count endpoint.
  // For the v1 administrators sub-tab the page size (50) is plenty
  // for visual context — admins paginate when they need more.
  const summary = useMemo(() => {
    let active = 0;
    let zombies = 0;
    let mfaVerified = 0;
    let selfSessions = 0;
    for (const row of items) {
      if (row.isActiveSysadmin) active++;
      else zombies++;
      if (row.mfaVerified) mfaVerified++;
      if (row.adminId === user.id) selfSessions++;
    }
    return { active, zombies, mfaVerified, selfSessions };
  }, [items, user.id]);

  const filtersDirty =
    draft.adminEmail !== (committed.adminEmail ?? '') ||
    draft.ipAddress !== (committed.ipAddress ?? '');

  const apply = () =>
    setCommitted({
      adminEmail: draft.adminEmail.trim() || undefined,
      ipAddress: draft.ipAddress.trim() || undefined,
    });

  const clear = () => {
    setDraft({ adminEmail: '', ipAddress: '' });
    setCommitted({});
  };

  // #1136 — TypedConfirmDialog's requireReason collects the
  // X-Step-Up-Reason. The mutation hook forwards it as the header.
  const handleRevoke = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    if (!revokeTarget) return;
    revoke.mutate(
      {
        id: revokeTarget.id,
        stepUpReason: payload.reason?.trim() ?? '',
      },
      {
        onSuccess: () => setRevokeTarget(null),
        // onError keeps the dialog closed (TypedConfirmDialog closes
        // itself on confirm); we surface the error via a toast in a
        // v1.1 follow-up. For now the next 10s poll will reflect the
        // actual state.
      },
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* Page heading — sub-tab parent already renders the rail nav, */}
      {/* so this is the section title only. */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <MonitorSmartphone className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Active sysadmin sessions
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.10</Badge>
              <Badge variant="secondary" className="text-xs">PR-E</Badge>
              <span className="text-xs text-muted-foreground">
                Refresh tokens for the <code className="text-[10px]">argusai-admin</code> audience.
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={
              isFetching
                ? 'h-3.5 w-3.5 mr-1.5 animate-spin'
                : 'h-3.5 w-3.5 mr-1.5'
            }
          />
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Active sessions"
          value={summary.active}
          icon={ShieldCheck}
          tone="success"
          hint="Sysadmin context, MFA-verified at login"
        />
        <StatTile
          label="Zombies"
          value={summary.zombies}
          icon={AlertTriangle}
          tone={summary.zombies > 0 ? 'destructive' : 'muted'}
          hint="Demoted admins with live refresh tokens"
        />
        <StatTile
          label="MFA verified"
          value={summary.mfaVerified}
          suffix={`/ ${items.length}`}
          tone="default"
        />
        <StatTile
          label="Your sessions"
          value={summary.selfSessions}
          tone="muted"
          hint="Logged in as your account"
        />
      </div>

      {/* Toolbar: flat filter row, no Card wrap (§11.5). */}
      <Toolbar sticky={false}>
        <Input
          value={draft.adminEmail}
          onChange={(e) =>
            setDraft((d) => ({ ...d, adminEmail: e.target.value }))
          }
          placeholder="Admin email contains…"
          className="h-9 text-xs max-w-xs"
          aria-label="Filter by admin email"
        />
        <Input
          value={draft.ipAddress}
          onChange={(e) =>
            setDraft((d) => ({ ...d, ipAddress: e.target.value }))
          }
          placeholder="IP address (exact)"
          className="h-9 text-xs max-w-xs font-mono"
          aria-label="Filter by IP address"
        />
        <Toolbar.Right>
          {filtersDirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setDraft({
                  adminEmail: committed.adminEmail ?? '',
                  ipAddress: committed.ipAddress ?? '',
                })
              }
            >
              Discard
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={clear}>
            <X className="h-3.5 w-3.5 mr-1" />
            Clear
          </Button>
          <Button size="sm" onClick={apply} disabled={!filtersDirty}>
            Apply
          </Button>
        </Toolbar.Right>
      </Toolbar>

      {/* Error surface */}
      {error && (
        <PageError
          title="Failed to load sysadmin sessions"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      )}

      {/* Body: flat table, no Card wrap. */}
      {!error && (
        <>
          {isLoading && items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
              Loading sysadmin sessions…
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={
                committed.adminEmail || committed.ipAddress
                  ? 'No sessions match the current filters'
                  : 'No active sysadmin sessions'
              }
              body={
                committed.adminEmail || committed.ipAddress
                  ? 'Clear filters to see every active session.'
                  : 'Nobody is logged in with a sysadmin token right now.'
              }
              action={
                committed.adminEmail || committed.ipAddress ? (
                  <Button variant="outline" size="sm" onClick={clear}>
                    Clear filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>User agent</TableHead>
                  <TableHead>MFA</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const isSelf = row.adminId === user.id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span
                            className="truncate max-w-[200px]"
                            title={row.adminEmail}
                          >
                            {row.adminEmail}
                          </span>
                          {isSelf && (
                            <Badge variant="outline" className="text-[10px]">
                              You
                            </Badge>
                          )}
                          {!row.isActiveSysadmin && (
                            <StatusPill
                              status="destructive"
                              icon={AlertTriangle}
                              compact
                            >
                              Zombie
                            </StatusPill>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {row.ipAddress ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div
                          className="truncate max-w-[260px] text-muted-foreground"
                          title={row.userAgent ?? '—'}
                        >
                          {row.userAgent ?? '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.mfaVerified ? (
                          <StatusPill status="healthy" icon={ShieldCheck} compact>
                            Verified
                          </StatusPill>
                        ) : (
                          <StatusPill
                            status="warning"
                            icon={AlertTriangle}
                            compact
                          >
                            Pending
                          </StatusPill>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {row.lastSeenAt
                          ? new Date(row.lastSeenAt).toLocaleString()
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {new Date(row.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {new Date(row.expiresAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Actions always visible per "Tooltips + visible
                            actions" feedback rule. No hover-only buttons. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRevokeTarget(row)}
                          title="Force-revoke this session"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {data?.nextCursor && (
            <div className="text-xs text-muted-foreground py-2 text-center">
              More sessions available. Narrow the filter to drill in.
            </div>
          )}
        </>
      )}

      {/* Revoke confirmation — typed-confirm requires the admin email
          to be typed exactly. Self-revoke is allowed (admin can log
          themselves out from another machine); the dialog still asks
          them to type their own email so a mis-click doesn't sign
          them out. */}
      {revokeTarget && (
        <TypedConfirmDialog
          open={!!revokeTarget}
          onOpenChange={(open) => !open && setRevokeTarget(null)}
          title="Revoke sysadmin session"
          description={
            revokeTarget.adminId === user.id
              ? "This is YOUR session. Revoking it will sign you out of this machine on the next API call."
              : `This will immediately sign out ${revokeTarget.adminEmail}.`
          }
          consequences={[
            'The refresh token is deleted from the database',
            'The next API call from this session returns 401',
            'The user must log in + re-MFA to start a new session',
            'A platform_audit_logs row records the revoke',
          ]}
          confirmPhrase={revokeTarget.adminEmail}
          confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke session'}
          requireReason
          reasonPlaceholder="Audit reason: incident reference / ticket (3-500 chars)"
          onConfirm={handleRevoke}
        />
      )}
    </div>
  );
}
